/**
 * Household sagas — async logic, API calls.
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import { getHousehold } from '@/infra/api/client';
import { SYNC_REFRESH } from '@/infra/sync/actions';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  setHousehold,
  setUsers,
  setCurrentUser,
  setHouseholdLoading,
  setHouseholdError,
  FETCH_HOUSEHOLD,
} from './ducks';
import { Household } from '@/infra/types/domain';

// Mirror auth LOGIN_SUCCESS into household.currentUserId so selectors that
// answer "which row is mine?" (family view split, partner read-only gating)
// have the caller's id. The auth slice is the source of truth for the login
// event; the household slice is where ownership-scoped UI reads from.
interface LoginSuccessAction {
  type: string;
  payload: { user: { id: string } };
}

// ─── Fetch Household ───────────────────────────────────────

export function* fetchHouseholdSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'household.fetch', ['household'], action, function* () {
    yield put(setHouseholdLoading(true));
    try {
      const household = (yield call(getHousehold)) as Household;
      yield* emitSagaPoint('api_getHousehold_ok', { member_count: household.members?.length ?? 0 });
      yield put(setHousehold(household));
      yield put(setUsers(household.members));
      yield put(setHouseholdError(null));
    } catch (error: any) {
      yield put(setHouseholdError(error.message || 'Failed to fetch household'));
      yield* emitSagaPoint('api_getHousehold_err', { message: error?.message });
    } finally {
      yield put(setHouseholdLoading(false));
    }
  });
}

// ─── WebSocket Event Handlers ──────────────────────────────

export function* handleWsHouseholdUpdated(action: {
  type: string;
  payload: Household;
}): Generator {
  yield put(setHousehold(action.payload));
  yield put(setUsers(action.payload.members));
}

export function* syncCurrentUserFromAuth(action: LoginSuccessAction): Generator {
  const userId = action.payload?.user?.id;
  if (userId) {
    yield put(setCurrentUser(userId));
    // Fetch the household so selectors that look up partner users by id
    // (family-view owner labels, read-only gating) have data to work with.
    yield put({ type: FETCH_HOUSEHOLD });
  }
}

// ─── Root Household Saga ───────────────────────────────────

export function* householdSaga(): Generator {
  yield takeLatest(FETCH_HOUSEHOLD, fetchHouseholdSaga);
  yield takeLatest('WS_HOUSEHOLD_UPDATED', handleWsHouseholdUpdated);
  yield takeLatest('auth/loginSuccess', syncCurrentUserFromAuth);
  yield takeLatest(SYNC_REFRESH, fetchHouseholdSaga);
}
